import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import useThemeColor from "../../hooks/useThemeColor.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import { logout } from "../../app/auth/authSlice.js";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import PageSizeDropDownSelection, {
  SortOrderDropDownSelection,
} from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import "./QuoteFilters.css";

const ManageQuotes = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' or 'edit'
  const [currentQuote, setCurrentQuote] = useState({
    qID: 0,
    qDetail: "",
    qIsActive: true,
    qPriority: false,
    qSortOrder: 0,
  });

  // Filter and pagination states
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [sortBy, setSortBy] = useState({ value: "QSortOrder", label: "Sort Order" });
  const [sortOrder, setSortOrder] = useState({ value: "ASC", label: "Asc" });
  const [activeFilter, setActiveFilter] = useState({ value: "all", label: "All Quotes" });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPageNumber(1);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [search]);

  const sortByOptions = [
    { value: "QID", label: "ID" },
    { value: "QDetail", label: "Quote Text" },
    { value: "QPriority", label: "Priority" },
    { value: "QSortOrder", label: "Sort Order" },
    { value: "CreatedDate", label: "Created Date" },
  ];

  const activeFilterOptions = [
    { value: "all", label: "All Quotes" },
    { value: "active", label: "Active Only" },
    { value: "inactive", label: "Inactive Only" },
  ];

  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_QUOTES, {
        params: {
          Search: debouncedSearch.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          PageSize: pageSize.value,
          PageNumber: pageNumber,
          ActiveOnly: activeFilter.value === "active" ? true : false,
        },
      });

      if (response.data.status) {
        const quoteData = response.data.data;
        if (Array.isArray(quoteData)) {
          setQuotes(quoteData);
          setTotalRecords(response.data.totalRecords || 0);
          setTotalPages(response.data.totalPages || 1);
        } else {
          setQuotes([]);
          setTotalRecords(0);
          setTotalPages(1);
        }
      } else {
        showErrorToast(response.data.message || "Failed to fetch quotes");
        setQuotes([]);
        setTotalRecords(0);
        setTotalPages(1);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to fetch quotes");
      }
    }
  }, [debouncedSearch, sortBy, sortOrder, pageSize, pageNumber, activeFilter, dispatch, navigate]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleOpenAddModal = () => {
    setModalMode("add");
    setCurrentQuote({
      qID: 0,
      qDetail: "",
      qIsActive: true,
      qPriority: false,
      qSortOrder: 0,
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (quote) => {
    setModalMode("edit");
    setCurrentQuote({
      qID: quote.qID,
      qDetail: quote.qDetail,
      qIsActive: quote.qIsActive,
      qPriority: quote.qPriority,
      qSortOrder: quote.qSortOrder,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentQuote({
      qID: 0,
      qDetail: "",
      qIsActive: true,
      qPriority: false,
      qSortOrder: 0,
    });
  };

  const handleInputChange = (e, field) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setCurrentQuote({ ...currentQuote, [field]: value });
  };

  const handleSaveQuote = async () => {
    if (!currentQuote.qDetail || currentQuote.qDetail.trim() === "") {
      showErrorToast("Please enter quote details");
      return;
    }

    try {
      setLoading(true);

      let payload;
      let response;

      if (modalMode === "add") {
        // For add mode, don't send QID
        payload = {
          QDetail: currentQuote.qDetail,
          QIsActive: currentQuote.qIsActive,
          QPriority: currentQuote.qPriority,
          QSortOrder: parseInt(currentQuote.qSortOrder) || 0,
        };
        response = await axiosInstance.post(APIs.POST_ADD_QUOTE, payload);
      } else {
        // For update mode, include QID
        payload = {
          QID: currentQuote.qID,
          QDetail: currentQuote.qDetail,
          QIsActive: currentQuote.qIsActive,
          QPriority: currentQuote.qPriority,
          QSortOrder: parseInt(currentQuote.qSortOrder) || 0,
        };
        response = await axiosInstance.put(APIs.PUT_UPDATE_QUOTE, payload);
      }

      if (response.data.status) {
        showSuccessToast(
          modalMode === "add"
            ? "Quote added successfully"
            : "Quote updated successfully"
        );
        handleCloseModal();
        fetchQuotes();
      } else {
        showErrorToast(
          response.data.message ||
            `Failed to ${modalMode === "add" ? "add" : "update"} quote`
        );
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      showErrorToast(`Failed to ${modalMode === "add" ? "add" : "update"} quote`);
    }
  };

  const handleDeleteQuote = async (qID) => {
    try {
      const result = await swal({
        title: "Are you sure?",
        text: "This will permanently delete the quote.",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        setLoading(true);
        const response = await axiosInstance.delete(APIs.DELETE_QUOTE, {
          params: { qID: qID },
        });

        if (response.data.status) {
          showSuccessToast("Quote deleted successfully");
          fetchQuotes();
        } else {
          showErrorToast(response.data.message || "Failed to delete quote");
        }
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      showErrorToast("Failed to delete quote");
    }
  };

  const handleToggleStatus = async (qID, currentStatus) => {
    try {
      setLoading(true);
      const response = await axiosInstance.patch(
        APIs.PATCH_UPDATE_QUOTE_STATUS,
        null,
        {
          params: {
            qID: qID,
            status: !currentStatus,
          },
        }
      );

      if (response.data.status) {
        showSuccessToast("Quote status updated successfully");
        fetchQuotes();
      } else {
        showErrorToast(response.data.message || "Failed to update status");
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      showErrorToast("Failed to update quote status");
    }
  };

  return (
    <div className="content">
      {loading && <Preloader />}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <Link to="/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Manage Quotes</li>
                </ol>
                <h1 className="page-header mb-0">Quotes Management</h1>
              </div>
              <div className="ms-auto">
                <button
                  className="btn px-4"
                  onClick={handleOpenAddModal}
                  style={{
                    backgroundColor: themeColor,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: "none",
                    color: "white",
                    borderRadius: "25px",
                    fontWeight: "500",
                  }}
                >
                  <i className="fa fa-plus fa-lg me-2 ms-n2"></i>
                  Add Quote
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* Enhanced Filter Section */}
                <div className="mb-4">
                  <div
                    className="card filter-card"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: `0 4px 20px ${themeColor}40`
                    }}
                  >
                    <div className="card-body p-3">
                      {/* Filter Controls */}
                      <div className="row g-3 align-items-center">
                        {/* Search Field */}
                        <div className="col-xl-4 col-lg-4 col-md-6">
                          <div className="search-input filter-control">
                            <input
                              type="text"
                              className="filter-input"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              placeholder="Search quotes..."
                              style={{
                                backgroundColor: isDarkMode ? 'rgba(45, 52, 54, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(255, 255, 255, 0.3)',
                                color: isDarkMode ? '#ffffff' : '#333',
                                borderRadius: '6px',
                                height: '36px'
                              }}
                            />
                          </div>
                        </div>

                        {/* Status Filter */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={activeFilter}
                              onChange={(option) => {
                                setActiveFilter(option);
                                setPageNumber(1);
                              }}
                              options={activeFilterOptions}
                              styles={dropdownStyles}
                              placeholder="Filter Status"
                              className="filter-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Sort By Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={sortBy}
                              onChange={(option) => {
                                setSortBy(option);
                                setPageNumber(1);
                              }}
                              options={sortByOptions}
                              styles={dropdownStyles}
                              placeholder="Sort By"
                              className="filter-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Sort Order Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <SortOrderDropDownSelection
                              defaultValue={sortOrder}
                              onChange={(option) => {
                                setSortOrder(option);
                                setPageNumber(1);
                              }}
                              styles={dropdownStyles}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Clear Button */}
                        <div className="col-xl-2 col-lg-2 col-md-12">
                          <button
                            className="clear-btn w-100"
                            onClick={() => {
                              setSearch("");
                              setActiveFilter({ value: "all", label: "All Quotes" });
                              setPageNumber(1);
                            }}
                            title="Clear all filters"
                            style={{
                              height: "38px",
                              backgroundColor: isDarkMode ? 'rgba(45, 52, 54, 0.8)' : 'rgba(255, 255, 255, 0.2)',
                              border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.3)',
                              color: 'white',
                              borderRadius: '6px'
                            }}
                          >
                            <i className="fa fa-times me-1"></i>
                            Clear Filters
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 5,
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      <tr>
                        <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Quote</th>
                        <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Priority</th>
                        <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Sort Order</th>
                        <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Status</th>
                        <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="5" className="text-center">
                            <Preloader />
                          </td>
                        </tr>
                      ) : quotes.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No quotes found
                          </td>
                        </tr>
                      ) : (
                        quotes.map((quote) => (
                          <tr key={quote.qID}>
                            <td>
                              <div className="d-flex align-items-start">
                                <i 
                                  className="fa fa-quote-left me-2 mt-1" 
                                  style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}
                                ></i>
                                <div className="flex-grow-1">{quote.qDetail}</div>
                              </div>
                            </td>
                            <td className="text-center">
                              {quote.qPriority ? (
                                <span className="badge bg-warning">
                                  <i className="fa fa-star me-1"></i>Priority
                                </span>
                              ) : (
                                <span className="badge bg-secondary">Normal</span>
                              )}
                            </td>
                            <td className="text-center">
                              <span className="badge bg-info">{quote.qSortOrder}</span>
                            </td>
                            <td className="text-center">
                              <div className="form-check form-switch d-inline-block">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={quote.qIsActive}
                                  onChange={() =>
                                    handleToggleStatus(quote.qID, quote.qIsActive)
                                  }
                                  style={{ cursor: "pointer" }}
                                />
                              </div>
                            </td>
                            <td className="text-center">
                              <div className="d-flex justify-content-center gap-2">
                                {/* Edit Button */}
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: `1px solid ${themeColor}`,
                                    color: themeColor,
                                    transition: 'all 0.2s ease',
                                    padding: 0
                                  }}
                                  onClick={() => handleOpenEditModal(quote)}
                                  title="Edit Quote"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = themeColor;
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = themeColor;
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                </button>

                                {/* Delete Button */}
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: '1px solid #dc3545',
                                    color: '#dc3545',
                                    transition: 'all 0.2s ease',
                                    padding: 0
                                  }}
                                  onClick={() => handleDeleteQuote(quote.qID)}
                                  title="Delete Quote"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#dc3545';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#dc3545';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <Pagination
                        currentPage={pageNumber}
                        totalPages={totalPages}
                        onPageChange={(page) => setPageNumber(page)}
                      />
                    </div>
                  </div>
                )}

                {/* Records Info */}
                <div className="row mt-3">
                  <div className="col-12">
                    <p style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                      Showing {quotes.length} of {totalRecords} quote(s)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Quote Modal */}
      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalMode === "add" ? "Add New Quote" : "Edit Quote"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                <Panel>
                  <PanelBody>
                    <div className="row mb-3">
                      <div className="col-12">
                        <label>Quote Details <span className="text-danger">*</span></label>
                        <textarea
                          className="form-control"
                          placeholder="Enter quote text..."
                          value={currentQuote.qDetail}
                          onChange={(e) => handleInputChange(e, "qDetail")}
                          rows="4"
                          style={{
                            borderRadius: "10px",
                            border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                            background: isDarkMode ? "#2d3436" : "#F9F9F9",
                            color: isDarkMode ? "#ffffff" : "#495057",
                          }}
                        />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-6">
                        <FormInput
                          label="Sort Order"
                          placeHolder="0"
                          type="number"
                          value={currentQuote.qSortOrder}
                          onchange={(e) => handleInputChange(e, "qSortOrder")}
                          min="0"
                        />
                        <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                          Lower numbers appear first
                        </small>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="qPriority"
                            checked={currentQuote.qPriority}
                            onChange={(e) => handleInputChange(e, "qPriority")}
                          />
                          <label className="form-check-label" htmlFor="qPriority">
                            Priority Quote
                          </label>
                        </div>
                        <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                          Priority quotes appear before normal quotes
                        </small>
                      </div>
                      <div className="col-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="qIsActive"
                            checked={currentQuote.qIsActive}
                            onChange={(e) => handleInputChange(e, "qIsActive")}
                          />
                          <label className="form-check-label" htmlFor="qIsActive">
                            Active
                          </label>
                        </div>
                      </div>
                    </div>
                  </PanelBody>
                </Panel>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveQuote}
                  disabled={loading}
                >
                  {modalMode === "add" ? "Add Quote" : "Update Quote"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageQuotes;

